import Headline from "../components/ui/headline";
import { ThemeTypeEnum } from "../assets/utils/validation/types";
import { HeadlineTypeEnum } from "../assets/utils/enums/headline";
import AppLayout from "../components/layout/app/layout";
import { useEffect, useState } from "react";
import {
  useChartStatisticType,
  useStatisticPeriod,
} from "../assets/store/useStatisticPeriod";
import FinanceChart, {
  FinanceChartProps,
} from "../components/ui/finance-chart";
import getData from "../api/project-request";
import {
  API_URL_CONTACT,
  API_URL_FINANCE,
  API_URL_PROJECT,
  API_URL_TASK,
  API_URL_USER,
} from "../constants";
import { currencies } from "../assets/utils/data/items";
import StatisticBlock, {
  DataStatisticProps,
} from "../components/ui/statistics-block";
import { COLORS } from "../assets/utils/enums/colors";
import lightTaskIcon from "../assets/images/icons/dark/tasks-icon.svg";
import basketIcon from "../assets/images/icons/basket-icon.svg";
import iconStatistic from "../assets/images/icons/pie-one-icon.svg";
import clientIcon from "../assets/images/icons/dark/clients-icon.svg";
import projectWhiteIcon from "../assets/images/icons/projects-white-icon.svg";
import { FinanceTypeTable } from "../assets/utils/enums/finance";
import { StatisticClientRow, StatisticUserItem } from "../assets/utils/table-types";
import { tableColumnType, TableTypeEnum } from "../assets/utils/enums/table";
import DataTable from "../components/ui/table";
import { TypeClientEnum } from "../assets/utils/enums/project";

const MainPage: React.FC = () => {
  const { period } = useStatisticPeriod();
  const { type: typeChartStat, setType: setTypeChartStat } =
    useChartStatisticType();
  const [graphicsFinChart, setGraphicsFinChart] = useState<FinanceChartProps>(
    {}
  );
  const [loaderState, setLoaderState] = useState<boolean>(false);
  const [taskStatistic, setTaskStatistic] = useState<DataStatisticProps[]>([]);
  const [projStatistic, setProjStatistic] = useState<DataStatisticProps[]>([]);
  const [sellStatistic, setSellStatistic] = useState<DataStatisticProps[]>([]);
  const [clientStatistic, setClientStatistic] = useState<DataStatisticProps[]>(
    []
  );
  const [mainFinStatistics, setMainFinStatistics] = useState<
    DataStatisticProps[]
  >([]);
  const [statisticClient, setStatisticClient] = useState<StatisticClientRow[]>(
    []
  );
  const [statisticUserItem, setStatisticUserItem] = useState<StatisticUserItem[]>(
    []
  );

  useEffect(() => {
    setLoaderState(true);

    const getClient = async () => {
      try {
        const data = await getData(`${API_URL_CONTACT}/get-all-clients`);

        if (Array.isArray(data)) {
          setStatisticClient(
            data.slice(0, 3).map(
              (item): StatisticClientRow => ({
                id: item.id,
                firstName: item.firstName,
                lastName: item.lastName,
                phone: item.phone,
                email: item.email,
                customerType: item.customerType as TypeClientEnum || TypeClientEnum.POTENTIAL_CLIENT,
                companyName: item.companyName,
              })
            )
          );
        } else {
          setStatisticClient([]);
        }
      } catch (error: any) {
        console.log(error.message);
        setStatisticClient([]);
      } finally {
        setTimeout(() => {
          setLoaderState(false);
        }, 100);
      }
    };

    getClient();
  }, [clientStatistic]);

  useEffect(() => {
    const getGraphicsChartStatistic = async () => {
      try {
        const data = await getData(
          `${API_URL_FINANCE}/get-${typeChartStat === FinanceTypeTable.INCOME ? "incomes" : "expenses"
          }-statistics?currency=${currencies[1].code}`
        );

        if (!data?.data) {
          setGraphicsFinChart({});
          return;
        }

        setGraphicsFinChart(data);
      } catch (error) {
        console.error("❌ Помилка при запиті статистики:", error);
        setGraphicsFinChart({});
      }
    };
    setLoaderState(true);
    setTimeout(() => {
      getGraphicsChartStatistic();
      setLoaderState(false);
    }, 100);
  }, [typeChartStat]);

  useEffect(() => {
    const getStatisticTask = async () => {
      const data = await getData(
        `${API_URL_TASK}/tasks-summary?period=${period}`
      );
      setTaskStatistic([
        {
          title: "Створені",
          value: data.totalCreated,
          percent: "",
        },
        {
          title: "Виконані",
          value: data.completed,
          percent: "",
        },
        { title: "В процесі", value: data.inProgress, percent: "" },
      ]);
    };

    const getStatisticProject = async () => {
      const data = await getData(`${API_URL_PROJECT}/summary?period=${period}`);
      setProjStatistic([
        { title: "Всі проєкти", value: data.totalProjects, percent: "" },
        {
          title: "Активні проєкти",
          value: data.activeProjects,
          percent: data.activeProjectsChange,
        },
      ]);
    };

    const getStatisticClient = async () => {
      const data = await getData(
        `${API_URL_CONTACT}/contact-statistic?period=${period}`
      );
      setClientStatistic([
        {
          title: "Потенційні",
          value: data.potentialCount,
          percent: data.potentialGrowthPercent,
        },
        {
          title: "Активні",
          value: data.activeCount,
          percent: data.activeGrowthPercent,
        },
        {
          title: "Втрачені",
          value: data.lostCount,
          percent: data.lostGrowthPercent,
        },
      ]);
    };

    const getStatisticFinance = async () => {
      const data = await getData(
        `${API_URL_FINANCE}/get-all-statistics?period=${period}&currency=${currencies[1].code}`
      );

      setMainFinStatistics([
        {
          title: "Надходження",
          value: data.formattedIncomes,
          percent: `${data.incomesChangePercent}`,
        },
        {
          title: "Витрати",
          value: data.formattedExpenses,
          percent: `${data.expensesChangePercent}`,
        },
      ]);
    };

    const getStatisticSelling = async () => {
      const data = await getData(
        `${API_URL_PROJECT}/selling-summary?period=${period}`
      );
      setSellStatistic([
        { title: "Всі продажі", value: data.totalSellings, percent: "" },
        { title: "В процесі", value: data.inProgressSellings, percent: "" },
        {
          title: "Завершені",
          value: data.completedSellings,
          percent: data.completedChangePercent,
        },
      ]);
    };

    const getStatisticUserItems = async () => {
      try {
        const data = await getData(
          `${API_URL_USER}/get-users-statistic-by-period?period=${period}`
        );

        if (Array.isArray(data)) {
          setStatisticUserItem(data);
          return;
        }
        else {
          setStatisticUserItem([]);
        }
        
      } catch (error) {
        console.error("❌ Помилка при запиті статистики:", error);
        setStatisticUserItem([]);
      }
    }

    setLoaderState(true);
    setTimeout(() => {
      getStatisticTask();
      getStatisticProject();
      getStatisticClient();
      getStatisticFinance();
      getStatisticSelling();
      getStatisticUserItems();

      setLoaderState(false);
    }, 100);
  }, [period]);

  return (
    <AppLayout className="" blockType="main" loaderState={loaderState}>
      <Headline
        headline={HeadlineTypeEnum.PANEL}
        theme={ThemeTypeEnum.LIGHT}
        btn_link="/client"
      />
      <div className="d-flex flex-column gap-3 pt-3">
        <div className="d-flex gap-3 w-100">
          <FinanceChart
            year={graphicsFinChart.year}
            currency={graphicsFinChart.currency}
            data={graphicsFinChart.data}
            typeTable={graphicsFinChart.typeTable}
            isDropdown={true}
            typeStat={typeChartStat}
            setTypeStat={setTypeChartStat}
          />
          <div className="w-40 d-flex flex-column justify-content-between gap-3">
            <StatisticBlock
              key={452}
              iconColor={COLORS.main2}
              iconUrl={lightTaskIcon}
              bgColor={COLORS.white}
              isDropdown={true}
              data={taskStatistic}
              statsTitle="Завдання"
            />
            <StatisticBlock
              key={453}
              iconColor={COLORS["light-bg-color"]}
              iconUrl={projectWhiteIcon}
              bgColor={COLORS.blue}
              isDropdown={true}
              data={projStatistic}
              statsTitle="Проєкти"
            />
            <StatisticBlock
              iconColor={COLORS.main1}
              iconUrl={clientIcon}
              bgColor={COLORS["light-gray"]}
              isDropdown={true}
              statsTitle="Клієнти"
              data={clientStatistic}
            />
          </div>
        </div>
        <div className="d-flex gap-3 w-100">
          <StatisticBlock
            iconColor={COLORS["bg-text-color"]}
            iconUrl={iconStatistic}
            bgColor={COLORS.blue}
            isDropdown={true}
            data={mainFinStatistics}
          />
          <StatisticBlock
            iconColor={COLORS.secondary1}
            iconUrl={basketIcon}
            bgColor={COLORS.white}
            isDropdown={true}
            data={sellStatistic}
          />
        </div>
        <DataTable
          table={tableColumnType[TableTypeEnum.STATISTIC_USER_ITEM]}
          data={statisticUserItem}
          itemsCount={3}
        />
        <DataTable
          table={tableColumnType[TableTypeEnum.STATISTIC_CLIENTS]}
          data={statisticClient}
        />
      </div>
    </AppLayout>
  );
};

export default MainPage;
